import { apiFetch } from "./apiFetch";
import { useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../context/AuthContext";
import { videoStatusPollInterval } from "./videoStatus.mjs";

export async function apiGet(path, signal) {
  const response = await apiFetch(`${process.env.API_URL}/api${path}`, { signal });
  const data = await response.json().catch(error => {
    if (response.ok) throw error;
    return {};
  });
  if (!response.ok) throw Object.assign(new Error(data.message || "Could not load data. Please try again."), { status: response.status });
  return data;
}

export function useAccountQuery(key, path, options = {}) {
  const { userId } = useAuth();
  const queryKey = ["account", userId, ...key];
  const query = useQuery({ queryKey, queryFn: ({ signal }) => apiGet(path, signal), enabled: Boolean(userId), ...options });
  const denied = [401, 403, 404].includes(query.error?.status);
  return { ...query, data: denied ? undefined : query.data, queryKey };
}

export function useGroupClasses(groupId) {
  const client = useQueryClient();
  const { userId } = useAuth();
  const query = useAccountQuery(["classes", groupId], `/getclasses/${groupId}`, {
    enabled: Boolean(userId && groupId),
    refetchInterval: videoStatusPollInterval,
    refetchIntervalInBackground: false,
  });
  const update = useCallback((field, value) => {
    const queryKey = ["account", userId, "classes", groupId];
    // A pending read must not overwrite a newer mutation/socket response.
    client.cancelQueries({ queryKey, exact: true });
    client.setQueryData(queryKey, previous => previous ? {
      ...previous, [field]: typeof value === "function" ? value(previous[field] || []) : value,
    } : previous);
    client.invalidateQueries({ queryKey: ["account", userId, "members", groupId] });
  }, [client, userId, groupId]);
  const setClasses = useCallback(value => update("classes", value), [update]);
  const setModules = useCallback(value => update("modules", value), [update]);
  return { ...query, setClasses, setModules };
}
